import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Bug, X, Copy, Trash2 } from 'lucide-react';

/**
 * DebugErrorButton — pallino (sotto il logo, Layout.js) che mostra gli ultimi errori API
 * intercettati dall'axios interceptor. Si attiva automaticamente quando
 * un errore arriva via evento `jvservice:api-error`.
 *
 * Mostra un dialog con: URL, method, status, payload inviato, response body,
 * con bottone "Copia tutto" per inviare allo sviluppatore.
 */
function DebugErrorButton() {
  const [hasError, setHasError] = useState(false);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  // Carica errori dal localStorage all'avvio
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('jvservice_api_errors') || '[]');
      setErrors(stored);
      if (stored.length > 0) setHasError(true);
    } catch (_) {}
  }, []);

  // Listener per nuovi errori dall'interceptor
  useEffect(() => {
    const handler = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('jvservice_api_errors') || '[]');
        setErrors(stored);
        setHasError(true);
      } catch (_) {}
    };
    window.addEventListener('jvservice:api-error', handler);
    return () => window.removeEventListener('jvservice:api-error', handler);
  }, []);

  const handleCopy = (err) => {
    const text = JSON.stringify(err, null, 2);
    navigator.clipboard?.writeText(text);
    alert('Errore copiato negli appunti. Incollalo allo sviluppatore.');
  };

  const handleCopyAll = () => {
    const text = JSON.stringify(errors, null, 2);
    navigator.clipboard?.writeText(text);
    alert('Tutti gli errori copiati negli appunti.');
  };

  const handleClear = () => {
    if (window.confirm('Cancellare la cronologia di tutti gli errori?')) {
      localStorage.removeItem('jvservice_api_errors');
      setErrors([]);
      setHasError(false);
    }
  };

  if (!hasError && !open) return null;

  return (
    <>
      {/* Bottone sotto il logo nella barra laterale (Layout.js) */}
      <button
        onClick={() => setOpen(true)}
        title="Mostra ultimi errori API"
        style={{
          position: 'relative', flexShrink: 0,
          width: 36, height: 36, borderRadius: '50%',
          background: errors.length > 0 ? '#dc2626' : '#94a3b8',
          color: 'white', border: 'none', cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18,
        }}
      >
        <Bug size={18} />
        {errors.length > 0 && (
          <span
            style={{
              position: 'absolute', top: -4, right: -4,
              background: 'white', color: '#dc2626',
              borderRadius: 10, padding: '2px 6px',
              fontSize: 11, fontWeight: 700,
              border: '2px solid #dc2626',
            }}
          >
            {errors.length}
          </span>
        )}
      </button>

      {/* Modale: in un portale sul body, la barra laterale (transform) confinerebbe il position:fixed */}
      {open && createPortal(
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 12,
              maxWidth: 900, width: '100%', maxHeight: '85vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Bug size={18} color="#dc2626" />
                Ultimi errori API ({errors.length})
              </h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleCopyAll}
                  disabled={errors.length === 0}
                  style={{
                    padding: '6px 12px', borderRadius: 6,
                    background: '#7c3aed', color: 'white',
                    border: 'none', cursor: 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <Copy size={14} /> Copia tutto
                </button>
                <button
                  onClick={handleClear}
                  disabled={errors.length === 0}
                  style={{
                    padding: '6px 12px', borderRadius: 6,
                    background: '#e2e8f0', color: '#334155',
                    border: 'none', cursor: 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  <Trash2 size={14} /> Svuota
                </button>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    width: 32, height: 32, borderRadius: 6,
                    background: '#f1f5f9', color: '#64748b',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Lista errori */}
            <div style={{ padding: 20, overflow: 'auto', flex: 1 }}>
              {errors.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40 }}>
                  Nessun errore registrato 🎉
                </div>
              ) : (
                errors.map((err, i) => (
                  <div key={i} style={{
                    border: '1px solid #fecaca', borderRadius: 8,
                    padding: 12, marginBottom: 12, background: '#fef2f2',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <span style={{
                          background: err.status >= 500 ? '#dc2626' : '#ea580c',
                          color: 'white', padding: '2px 8px', borderRadius: 4,
                          fontSize: 11, fontWeight: 700,
                        }}>
                          {err.status || '?'} {err.statusText || ''}
                        </span>
                        <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 12, color: '#0f172a', fontWeight: 600 }}>
                          {err.method} {err.url}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                          {new Date(err.timestamp).toLocaleTimeString('it-IT')}
                        </span>
                        <button
                          onClick={() => handleCopy(err)}
                          style={{
                            padding: '2px 8px', borderRadius: 4,
                            background: 'white', color: '#7c3aed',
                            border: '1px solid #7c3aed', cursor: 'pointer',
                            fontSize: 11, display: 'flex', alignItems: 'center', gap: 2,
                          }}
                        >
                          <Copy size={11} /> Copia
                        </button>
                      </div>
                    </div>
                    {err.message && (
                      <div style={{ fontSize: 13, color: '#7f1d1d', marginBottom: 6 }}>
                        💬 {err.message}
                      </div>
                    )}
                    {err.requestPayload && (
                      <details style={{ marginTop: 6 }}>
                        <summary style={{ cursor: 'pointer', color: '#7c3aed', fontSize: 12, fontWeight: 600 }}>
                          Request payload
                        </summary>
                        <pre style={{
                          background: 'white', padding: 8, borderRadius: 4,
                          fontSize: 11, overflow: 'auto', maxHeight: 200, marginTop: 4,
                        }}>{JSON.stringify(err.requestPayload, null, 2)}</pre>
                      </details>
                    )}
                    {err.responseBody && (
                      <details style={{ marginTop: 6 }} open>
                        <summary style={{ cursor: 'pointer', color: '#7c3aed', fontSize: 12, fontWeight: 600 }}>
                          Response body
                        </summary>
                        <pre style={{
                          background: 'white', padding: 8, borderRadius: 4,
                          fontSize: 11, overflow: 'auto', maxHeight: 200, marginTop: 4,
                        }}>{JSON.stringify(err.responseBody, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default DebugErrorButton;
